import PropTypes from 'prop-types';
import { fromUnixTime, isFuture } from 'date-fns';
import { applyCouponOverwrites, getCouponDiscount } from '../../../utils/coupon-utils';

/**
 * @typedef {object} CouponProps
 * @property {string} id
 * @property {number} percent_off
 * @property {number} amount_off
 * @property {'forever'|'once'|'repeating'} duration
 * @property {number} duration_in_months
 * @property {boolean} valid
 */

/**
 * @typedef {object} DiscountSectionProps
 * @property {object} discount
 * @property {number} discount.end
 * @property {number} discount.start
 * @property {CouponProps} discount.coupon
 */

/**
 * @param {DiscountSectionProps} discount
 * @returns {JSX.Element}
 */
export default function DiscountSection({ discount }) {
    if (discount?.coupon) {
        const endDate = discount?.end ? new Date(fromUnixTime(discount.end)) : null;
        const startDate = discount?.start ? new Date(fromUnixTime(discount.start)) : null;
        // Timestamp comparisons instead of day-difference math: differenceInDays
        // truncates to whole days, which hid the discount during its final
        // partial day and inverted the start check for past start dates.
        const hasStarted = startDate ? !isFuture(startDate) : true;
        const hasNotEnded = endDate ? isFuture(endDate) : false;
        const updatedDiscount = applyCouponOverwrites(discount);
        const discountText = `-${getCouponDiscount({ coupon: updatedDiscount.coupon, format: true, calculateTotal: false })}`;

        // Three different types of coupons: forever, once, repeating
        // We need to check if the coupon is still valid and if it has started yet
        // We also need to check if the coupon is still valid if it's a repeating coupon
        const isValidForever =
            discount?.coupon?.duration === 'forever' && hasStarted && discount.coupon.valid;
        const isValidOnce =
            discount?.coupon?.duration === 'once' && hasStarted && discount.coupon.valid;
        const isStillValid =
            discount?.coupon?.duration === 'repeating' &&
            hasStarted &&
            hasNotEnded &&
            discount.coupon.valid;

        if (isValidForever || isValidOnce || isStillValid) {
            return (
                <div data-testid="discount-section">
                    <strong>Special offer active</strong>
                    <strong className="green" data-testid="discount-text">
                        {discountText}
                    </strong>
                </div>
            );
        } else {
            return null;
        }
    } else {
        return null;
    }
}

DiscountSection.propTypes = {
    discount: PropTypes.shape({
        end: PropTypes.number,
        start: PropTypes.number,
        coupon: PropTypes.shape({
            id: PropTypes.string,
            percent_off: PropTypes.number,
            amount_off: PropTypes.number,
            duration: PropTypes.string.isRequired,
            duration_in_months: PropTypes.number,
            valid: PropTypes.bool.isRequired,
        }),
    }),
};
